import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide6Mobile from "./HTLDSlide6Mobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide6Content, useSlide6, Seg } from "./slide6.data";

const accent = "#38b5b5";
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
root: {
background: "#040f18", fontFamily: font, color: "#d0e8f8",
width: "100%", height: "100%",
display: "flex", flexDirection: "column",
boxSizing: "border-box",
overflow: "hidden",
},
// Shared header row: breadcrumb (left) + slide title (right), above the divider line
subtitleBar: {
background: "#050f1a", borderBottom: "1px solid rgba(56,181,181,0.1)",
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
flex: 1,
minHeight: 0,
display: "flex", gap: "clamp(16px,2vw,28px)",
padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
alignItems: "stretch",
overflow: "hidden",
},
content: {
flex: 1, minWidth: "min(100%, 400px)",
display: "flex", flexDirection: "column",
justifyContent: "space-between",
},
panel: {
width: "clamp(220px,28vw,400px)", flexShrink: 0,
display: "flex", flexDirection: "column",
},
};

const sleepData: { t: number; s: number }[] = [
{t:0.00,s:0},{t:0.18,s:2},{t:0.38,s:3},{t:0.95,s:3},{t:1.10,s:2},{t:1.30,s:1},
{t:1.65,s:2},{t:1.85,s:3},{t:2.30,s:3},{t:2.50,s:2},{t:2.70,s:1},{t:3.15,s:2},
{t:3.30,s:3},{t:3.65,s:3},{t:3.85,s:2},{t:4.05,s:1},{t:4.70,s:2},{t:4.88,s:3},
{t:5.10,s:2},{t:5.28,s:1},{t:6.08,s:2},{t:6.25,s:1},{t:7.10,s:2},{t:7.30,s:1},
{t:7.90,s:2},{t:8.00,s:0},
];

// ─── Sleep Graph Canvas ──────────────────────────────────────────────────────
const SleepGraph: React.FC = () => {
const canvasRef = useRef<HTMLCanvasElement>(null);
const drawFnRef = useRef<null | (() => void)>(null);
const dtRef     = useRef<number>(0);
useEffect(() => {
  const canvas = canvasRef.current; if (!canvas) return;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
  const W = 460, H = 560;
  const c = pick(slide6Content).canvas; // slides remount on language change

  // ── DPR fix — eliminates blurry/soft rendering on retina screens ────────
  const dpr = window.devicePixelRatio || 1;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  ctx.scale(dpr, dpr);

  const GX = 62, GY = 55, GW = 368, GH = 195, TOTAL_H = 8;
  const stageY: Record<number, number> = { 0:GY, 1:GY+GH*0.28, 2:GY+GH*0.58, 3:GY+GH };
  const stageLabels = c.stageLabels;
  const tx = (t: number) => GX + (t / TOTAL_H) * GW;

  const pts: { x: number; y: number }[] = [];
  sleepData.forEach((d, i) => {
    const x = tx(d.t), y = stageY[d.s];
    if (i > 0) pts.push({ x, y: stageY[sleepData[i-1].s] });
    pts.push({ x, y });
  });

  const ANIM_FRAMES = 750;
  let frame = 0;

  const starSeeds = [[25,22,1.0],[88,12,0.8],[350,18,1.1],[420,45,0.7],[45,72,0.9],[390,68,1.0],[445,14,1.0],[130,48,0.8]];
  const stars = [
    ...starSeeds.map(([x,y,r]) => ({ x, y, r, tw: Math.random()*Math.PI*2, spd: 0.010+Math.random()*0.022 })),
    ...Array.from({ length: 32 }, () => ({
      x: Math.random()*W, y: Math.random()*(GY-8)+4,
      r: 0.3+Math.random()*0.9,
      tw: Math.random()*Math.PI*2, spd: 0.010+Math.random()*0.022,
    })),
  ];

  const dotTrail: { x: number; y: number }[] = [];
  const TRAIL = 22;

  function dotAt(prog: number) {
    const maxX = GX + prog * GW;
    for (let i = 0; i < pts.length - 1; i++) {
      if (pts[i].x <= maxX && pts[i+1].x > maxX) {
        const f = (maxX - pts[i].x) / (pts[i+1].x - pts[i].x);
        return { x: maxX, y: pts[i].y + f * (pts[i+1].y - pts[i].y) };
      }
    }
    return pts[pts.length - 1];
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);

    const pulse = 0.5 + 0.5 * Math.sin(frame * 0.14);

    // ── Stars (twinkling) ────────────────────────────────────────────────
    stars.forEach(s => {
      const tw = 0.25 + 0.75 * Math.abs(Math.sin(frame * s.spd + s.tw));
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI*2);
      ctx.fillStyle = `rgba(255,255,255,${(tw*0.75).toFixed(2)})`; ctx.fill();
    });

    // ── Moon ─────────────────────────────────────────────────────────────
    [[20,0.07],[13,0.13],[8,1]].forEach(([r,a],i) => {
      ctx.beginPath(); ctx.arc(415,38,r,0,Math.PI*2);
      ctx.fillStyle = i<2 ? `rgba(255,252,180,${a})` : "#fffde7"; ctx.fill();
    });

    // ── Graph background + grid ──────────────────────────────────────────
    ctx.fillStyle="rgba(56,181,181,0.04)"; ctx.fillRect(GX,GY,GW,GH);
    [0,1,2,3].forEach(s => {
      ctx.beginPath(); ctx.moveTo(GX,stageY[s]); ctx.lineTo(GX+GW,stageY[s]);
      ctx.strokeStyle="rgba(56,181,181,0.12)"; ctx.lineWidth=1; ctx.setLineDash([4,6]); ctx.stroke(); ctx.setLineDash([]);
    });

    // ── Time ticks ───────────────────────────────────────────────────────
    for (let h = 0; h <= TOTAL_H; h++) {
      const x = tx(h);
      ctx.beginPath(); ctx.moveTo(x,GY+GH); ctx.lineTo(x,GY+GH+5);
      ctx.strokeStyle="rgba(56,181,181,0.40)"; ctx.lineWidth=1; ctx.stroke();
      ctx.font="11px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle="rgba(120,210,210,0.90)";
      ctx.textAlign="center"; ctx.fillText(`${h}h`,x,GY+GH+16);
    }

    // ── Stage labels ─────────────────────────────────────────────────────
    stageLabels.forEach((label,i) => {
      ctx.font="bold 11px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = i===1 ? accent : "rgba(120,210,210,0.85)";
      ctx.textAlign="right"; ctx.fillText(label.toUpperCase(), GX-8, stageY[i]+3);
    });

    // ── Stage number sub-labels (N1–N2 under LIGHT, N3 under DEEP) ───────────
    const stageNums: Record<number, string> = { 2: "N1–N2", 3: "N3" };
    [2, 3].forEach(i => {
    ctx.font = "7px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle = "rgba(100,200,200,0.58)";
    ctx.textAlign = "right";
    ctx.fillText(stageNums[i], GX-8, stageY[i]+13);
    });
      // ── NREM bracket — groups Light + Deep on Y-axis ─────────────────────────
      const nTop = stageY[2] - 4;
      const nBot = stageY[3] + 4;
      const nMid = (nTop + nBot) / 2;
      
      ctx.strokeStyle = "rgba(120,210,210,0.50)"; ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(7, nTop); ctx.lineTo(7, nBot);    // vertical bar
      ctx.moveTo(7, nTop); ctx.lineTo(11, nTop);   // top tick
      ctx.moveTo(7, nBot); ctx.lineTo(11, nBot);   // bottom tick
      ctx.stroke();
      
      ctx.save();
      ctx.translate(5, nMid);
      ctx.rotate(-Math.PI / 2);
      ctx.font = "bold 7px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = "rgba(120,210,210,0.65)";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("NREM", 0, 0);
      ctx.restore();

    // ── Axes ─────────────────────────────────────────────────────────────
    ctx.beginPath(); ctx.moveTo(GX,GY-10); ctx.lineTo(GX,GY+GH+8); ctx.moveTo(GX,GY+GH+8); ctx.lineTo(GX+GW,GY+GH+8);
    ctx.strokeStyle="rgba(56,181,181,0.35)"; ctx.lineWidth=1; ctx.stroke();

    // ── REM bands ────────────────────────────────────────────────────────
    let inRem=false, remStart=0;
    sleepData.forEach(d => {
      if (d.s===1&&!inRem) { inRem=true; remStart=tx(d.t); }
      if (d.s!==1&&inRem) {
        inRem=false;
        ctx.fillStyle="rgba(56,181,181,0.14)"; ctx.fillRect(remStart,GY,tx(d.t)-remStart,GH);
        ctx.font="bold 7px 'JetBrains Mono','Courier New',monospace";
        ctx.fillStyle="rgba(120,220,220,0.90)";
        ctx.textAlign="center"; ctx.fillText("REM",(remStart+tx(d.t))/2,stageY[1]-5);
      }
    });
    if (inRem) { ctx.fillStyle="rgba(56,181,181,0.14)"; ctx.fillRect(remStart,GY,tx(sleepData[sleepData.length-1].t)-remStart,GH); }

    // ── Area fill under graph line ────────────────────────────────────────
    ctx.save();
    ctx.beginPath();
    pts.forEach((p,i) => i===0 ? ctx.moveTo(p.x,p.y) : ctx.lineTo(p.x,p.y));
    ctx.lineTo(pts[pts.length-1].x, GY+GH); ctx.lineTo(pts[0].x, GY+GH); ctx.closePath();
    const aGrd = ctx.createLinearGradient(0, GY, 0, GY+GH);
    aGrd.addColorStop(0,    "rgba(56,181,181,0.28)");
    aGrd.addColorStop(0.55, "rgba(56,181,181,0.10)");
    aGrd.addColorStop(1,    "rgba(56,181,181,0.02)");
    ctx.fillStyle = aGrd; ctx.fill();
    ctx.restore();

    // ── Line glow passes ─────────────────────────────────────────────────
    ctx.beginPath();
    pts.forEach((p,i) => i===0 ? ctx.moveTo(p.x,p.y) : ctx.lineTo(p.x,p.y));
    ctx.strokeStyle="rgba(56,181,181,0.18)"; ctx.lineWidth=10; ctx.lineJoin="round"; ctx.stroke();
    ctx.strokeStyle="rgba(56,181,181,0.32)"; ctx.lineWidth=4.5; ctx.lineJoin="round"; ctx.stroke();

    // ── Main graph line ───────────────────────────────────────────────────
    ctx.beginPath();
    pts.forEach((p,i) => i===0 ? ctx.moveTo(p.x,p.y) : ctx.lineTo(p.x,p.y));
    ctx.strokeStyle=accent; ctx.lineWidth=2; ctx.lineJoin="round"; ctx.stroke();

    // ── Animated dot ─────────────────────────────────────────────────────
    const prog = (frame % ANIM_FRAMES) / ANIM_FRAMES;
    const dot  = dotAt(prog);

    // Vertical position indicator
    ctx.beginPath(); ctx.moveTo(dot.x, GY); ctx.lineTo(dot.x, GY+GH);
    ctx.strokeStyle=`rgba(56,181,181,${(0.10+0.06*pulse).toFixed(3)})`;
    ctx.lineWidth=1; ctx.setLineDash([2,4]); ctx.stroke(); ctx.setLineDash([]);

    // Trail
    dotTrail.unshift({ x: dot.x, y: dot.y });
    if (dotTrail.length > TRAIL) dotTrail.pop();
    dotTrail.forEach((tp, ti) => {
      const ta = (1-ti/TRAIL)*0.45, tr = 3.5*(1-ti/TRAIL);
      if (tr > 0.3) {
        ctx.beginPath(); ctx.arc(tp.x, tp.y, tr, 0, Math.PI*2);
        ctx.fillStyle = `rgba(56,181,181,${ta.toFixed(3)})`; ctx.fill();
      }
    });

    // Dot glow
    const dg = ctx.createRadialGradient(dot.x,dot.y,0,dot.x,dot.y,20+pulse*5);
    dg.addColorStop(0, `rgba(56,181,181,${(0.55*pulse).toFixed(3)})`); dg.addColorStop(1,"rgba(56,181,181,0)");
    ctx.beginPath(); ctx.arc(dot.x,dot.y,20+pulse*5,0,Math.PI*2); ctx.fillStyle=dg; ctx.fill();
    ctx.beginPath(); ctx.arc(dot.x,dot.y,6+pulse*1.5,0,Math.PI*2); ctx.fillStyle="rgba(56,181,181,0.40)"; ctx.fill();
    ctx.beginPath(); ctx.arc(dot.x,dot.y,3.5,0,Math.PI*2); ctx.fillStyle=accent; ctx.fill();
    ctx.beginPath(); ctx.arc(dot.x,dot.y,1.5,0,Math.PI*2); ctx.fillStyle="#ffffff"; ctx.fill();

    // ── Graph title ───────────────────────────────────────────────────────
    ctx.font="bold 13px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="rgba(190,235,235,0.95)";
    ctx.textAlign="center"; ctx.fillText(c.title,GX+GW/2,GY-18);
    ctx.font="11px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="rgba(120,210,210,0.80)";
    ctx.fillText(c.timeAxis,GX+GW/2,GY+GH+30);

    // ── Cycle markers ─────────────────────────────────────────────────────
    [{start:0,end:1.65,label:`${c.cycle} 1`},{start:1.65,end:3.30,label:`${c.cycle} 2`},{start:3.30,end:5.28,label:`${c.cycle} 3`},{start:5.28,end:7.10,label:`${c.cycle} 4`}].forEach(cy => {
      const x1=tx(cy.start)+2, x2=tx(cy.end)-2, by=GY+GH+40;
      ctx.beginPath(); ctx.moveTo(x1,by); ctx.lineTo(x1,by+4); ctx.lineTo(x2,by+4); ctx.lineTo(x2,by);
      ctx.strokeStyle="rgba(56,181,181,0.30)"; ctx.lineWidth=1; ctx.stroke();
      ctx.font="8px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle="rgba(120,210,210,0.75)";
      ctx.textAlign="center"; ctx.fillText(cy.label,(x1+x2)/2,by+14);
    });

    // ══════════════════════════════════════════════════════════════════════
    // BELOW-GRAPH SECTION
    // ══════════════════════════════════════════════════════════════════════
    const BY = GY + GH + 82;

    ctx.font="bold 11px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="rgba(190,235,235,0.95)";
    ctx.textAlign="left"; ctx.fillText(c.distTitle, GX, BY);

    const stagesDist = [
      { name:c.distNames[0], pct:5,  mins:24,  bar:"rgba(56,181,181,0.30)", htld:false },
      { name:c.distNames[1], pct:50, mins:240, bar:"rgba(56,181,181,0.38)", htld:false },
      { name:c.distNames[2], pct:23, mins:110, bar:"rgba(56,181,181,0.55)", htld:false },
      { name:c.distNames[3], pct:22, mins:105, bar:accent,                   htld:true  },
    ];
    const BAR_X = GX + 50, BAR_MAX = GW - 105;

    stagesDist.forEach((s, i) => {
      const by = BY + 13 + i * 26;
      const fw = (s.pct / 100) * BAR_MAX;

      ctx.font = s.htld ? "bold 11px 'JetBrains Mono','Courier New',monospace" : "11px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = s.htld ? accent : "rgba(150,220,220,0.90)";
      ctx.textAlign="right"; ctx.fillText(s.name, BAR_X-6, by+9);

      ctx.fillStyle="rgba(56,181,181,0.08)"; ctx.fillRect(BAR_X, by, BAR_MAX, 14);

      if (s.htld) {
        const g = ctx.createLinearGradient(BAR_X,0,BAR_X+fw,0);
        g.addColorStop(0,"rgba(56,181,181,0.40)"); g.addColorStop(1,accent);
        ctx.fillStyle=g;
        ctx.shadowColor=accent; ctx.shadowBlur=7*pulse;
      } else {
        ctx.fillStyle=s.bar;
      }
      ctx.fillRect(BAR_X, by, fw, 14);
      ctx.shadowBlur=0;

      ctx.font="11px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = s.htld ? accent : "rgba(150,220,220,0.88)";
      ctx.textAlign="left"; ctx.fillText(`${s.pct}%  ~${s.mins}min`, BAR_X+fw+6, by+9);
    });

    // ── Stage insights ─────────────────────────────────────────────────────
    const IY = BY + 13 + 4*26 + 12;
    ctx.font="bold 11px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="rgba(190,235,235,0.95)";
    ctx.textAlign="left"; ctx.fillText(c.insightsTitle, GX, IY);

    const insights = [
      { ...c.insights[0], color:"rgba(120,215,215,0.92)"},
      { ...c.insights[1], color:accent},
      { ...c.insights[2], color:"rgba(120,215,215,0.80)"},
      ];

    insights.forEach((ins, i) => {
      const iy = IY + 13 + i * 24;

      ctx.font = ins.tag.includes("REM") ? "bold 11px 'JetBrains Mono','Courier New',monospace" : "11px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = ins.color; ctx.textAlign="left";
      if (ins.tag.includes("REM")) { ctx.shadowColor=accent; ctx.shadowBlur=6; }
      ctx.fillText(ins.tag, GX, iy+7);
      ctx.shadowBlur=0;

      ctx.beginPath(); ctx.arc(GX+42, iy+3.5, 1.5, 0, Math.PI*2);
      ctx.fillStyle="rgba(56,181,181,0.35)"; ctx.fill();

      ctx.font="11px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = ins.tag.includes("REM") ? "rgba(230,248,248,0.95)" : "rgba(170,228,228,0.82)";
      ctx.fillText(ins.text, GX+50, iy+7);

      ctx.beginPath(); ctx.moveTo(GX,iy+15); ctx.lineTo(GX+GW,iy+15);
      ctx.strokeStyle="rgba(56,181,181,0.08)"; ctx.lineWidth=1; ctx.stroke();
    });

    frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
  }

  drawFnRef.current = draw;
  return () => { drawFnRef.current = null; };
}, []);

useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

return (
  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
    <canvas
      ref={canvasRef}
      style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
    />
  </div>
);
};

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide6Inner: React.FC = () => {
const { meta, stages, callout } = useSlide6();
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
const existing = document.querySelector(
  'link[href*="Space+Grotesk"]'
) as HTMLLinkElement | null;

if (!existing) {
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
  document.head.appendChild(link);
  fontLinkRef.current = link;
}

return () => {
  if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
    document.head.removeChild(fontLinkRef.current);
    fontLinkRef.current = null;
  }
};
}, []);

const lbl: CSSProperties = {
fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
color: accent, letterSpacing: 3, textTransform: "uppercase",
marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
};
const txt: CSSProperties = {
fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
lineHeight: 1.7, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null)

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
      fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.titlePlain} <span style={{ color: accent }}>{meta.titleAccent}</span>
    </div>
  </div>

  {/* Body */}
  <div style={S.body}>

    {/* Content */}
    <div style={S.content}>

      {/* Title block */}
      <div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
          {meta.titlePlain} <span style={{ color: accent }}>{meta.titleAccent}</span>
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0, letterSpacing: 0.3 }}>
          {meta.subtitle}
        </p>
      </div>

      {/* Stage cards */}
      <div style={{ borderBottom: "1px solid rgba(56,181,181,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
        <div style={lbl}>{meta.stagesLabel}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "clamp(5px,0.7vw,9px)" }}>
          {stages.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: "clamp(10px,1.2vw,16px)", alignItems: "flex-start", background: s.rem ? "rgba(56,181,181,0.07)" : "rgba(255,255,255,0.02)", border: `1px solid ${s.rem ? "rgba(56,181,181,0.28)" : "rgba(56,181,181,0.08)"}`, borderRadius: 7, padding: "clamp(7px,0.9vw,11px) clamp(10px,1.1vw,14px)" }}>
              <div style={{ flexShrink: 0, width: "clamp(36px,3.6vw,50px)" }}>
                <div style={{ fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO, color: s.rem ? accent : "rgba(56,181,181,0.55)", letterSpacing: 1 }}>{s.stage}</div>
                <div style={{ fontSize: "clamp(8px,0.65vw,10px)", fontFamily: FONT_MONO, color: "#4a6880", marginTop: 2 }}>{s.duration}</div>
              </div>
              <div>
                <div style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontWeight: 700, color: s.rem ? "#a0e4e4" : "#c8dff0", marginBottom: 2 }}>{s.type}</div>
                <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 300, lineHeight: 1.55, color: s.rem ? "#88d8d8" : "#9bbdd6", margin: 0 }}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Callout */}
      <div style={{ background: "rgba(56,181,181,0.06)", border: "1px solid rgba(56,181,181,0.20)", borderRadius: 8, padding: "clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)" }}>
        <div style={lbl}>{meta.calloutLabel}</div>
        <p style={{ ...txt, color: "#9adcdc" }}>{renderSegs(callout, "#c8f0f0")}</p>
      </div>

    </div>

    {/* Panel — sleep graph fills the full panel height */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <SleepGraph />
      </div>
    </div>

  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide6: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide6Mobile />
    : <ScaleToFit><HTLDSlide6Inner /></ScaleToFit>;
};

export default HTLDSlide6;